import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import bloom220Img from "../assets/images/BLOOM220SOLITO.png";

const WHATSAPP_NUMBER = "51994692096";
function waLink(message) {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
}

/** Reveal suave sin librerías */
function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShow(true);
          obs.disconnect();
        }
      },
      { threshold: 0.14 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={[
        "transition-all duration-700 ease-out will-change-transform",
        show ? "opacity-100 translate-y-0 blur-0" : "opacity-0 translate-y-8 blur-[8px]",
        className,
      ].join(" ")}
    >
      {children}
    </div>
  );
}

function Divider({ tone = "light" }) {
  return (
    <div className="mx-auto max-w-7xl px-6 py-14">
      <div className={tone === "dark" ? "h-px bg-white/10" : "h-px bg-black/5"} />
    </div>
  );
}

export default function Bloom220() {
  // Un solo CTA WhatsApp (inicio). Sin repetir en el resto.
  const waMsg =
    "Hola, deseo cotizar Gelatina Bloom 220. Cantidad: __ kg. Presentación: __. Ciudad: __. (Si aplica: uso/proceso: __).";

  return (
    <main className="bg-white text-slate-900">
      {/* ========= HERO (Apple-like: aire + un solo CTA) ========= */}
      <section className="mx-auto max-w-7xl px-6 pt-10 md:pt-14">
        <div className="flex items-center justify-between">
          <Link
            to="/gelatinas"
            className="text-sm font-medium text-slate-600 hover:text-slate-900"
          >
            ← Volver a Gelatinas
          </Link>

          {/* ÚNICO botón de venta por WhatsApp */}
          <a
            href={waLink(waMsg)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex rounded-full bg-blue-600 px-6 py-3 text-[14px] font-medium text-white shadow-sm transition hover:bg-blue-700 active:scale-[0.99]"
          >
            Comprar por WhatsApp
          </a>
        </div>

        <div className="mt-14 grid gap-10 md:grid-cols-12 md:items-end">
          <div className="md:col-span-6">
            <Reveal>
              <p className="text-xs font-semibold tracking-[0.18em] text-slate-500">
                GELATINA INDUSTRIAL
              </p>

              <h1 className="mt-4 text-5xl md:text-7xl font-semibold tracking-tight">
                Bloom 220
              </h1>

              <p className="mt-8 max-w-xl text-lg md:text-xl leading-relaxed text-slate-600">
                Un grado diseñado para formulaciones que buscan{" "}
                <span className="text-slate-900 font-medium">
                  estructura consistente
                </span>{" "}
                y buena trabajabilidad en proceso.
              </p>
            </Reveal>

            {/* Micro-datos, sin “chips” ni cajitas */}
            <Reveal delay={120} className="mt-12 space-y-6">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold tracking-[0.18em] text-slate-500">
                    USO PRINCIPAL
                  </p>
                  <p className="mt-2 text-base text-slate-700 leading-relaxed">
                    Gelificante y estabilizante para aplicaciones alimentarias e industriales.
                  </p>
                </div>

                <div>
                  <p className="text-xs font-semibold tracking-[0.18em] text-slate-500">
                    DOCUMENTACIÓN
                  </p>
                  <p className="mt-2 text-base text-slate-700 leading-relaxed">
                    Ficha técnica (FT) y hoja de seguridad (MSDS) disponibles.
                  </p>
                </div>
              </div>

              <div className="h-px bg-black/5" />

              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold tracking-[0.18em] text-slate-500">
                    PRESENTACIÓN
                  </p>
                  <p className="mt-2 text-base text-slate-700 leading-relaxed">
                    Venta por kg y por saco (según disponibilidad).
                  </p>
                </div>

                <div>
                  <p className="text-xs font-semibold tracking-[0.18em] text-slate-500">
                    MESH / GRANULOMETRÍA
                  </p>
                  <p className="mt-2 text-base text-slate-700 leading-relaxed">
                    Se recomienda según velocidad de disolución y el proceso de producción.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Imagen sin ratio (recorte Apple-style) */}
          <div className="md:col-span-6">
            <Reveal delay={80}>
              <div className="relative overflow-hidden rounded-[34px]">
                <div className="pointer-events-none absolute -inset-24 bg-[radial-gradient(circle_at_25%_20%,rgba(99,102,241,0.18),transparent_55%),radial-gradient(circle_at_80%_35%,rgba(14,165,233,0.14),transparent_55%),radial-gradient(circle_at_55%_85%,rgba(253,230,138,0.14),transparent_60%)]" />
                <div className="relative h-[440px] md:h-[520px] lg:h-[580px]">
                  <img
                    src={bloom220Img}
                    alt="Gelatina Bloom 220"
                    className="h-full w-full object-cover object-center"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Divider />

      {/* ========= STORY 1 (color suave, profesional) ========= */}
      <section className="bg-gradient-to-b from-[#fbf7ef] via-white to-white">
        <div className="mx-auto max-w-7xl px-6 py-20 md:py-28">
          <Reveal>
            <p className="text-xs font-semibold tracking-[0.18em] text-slate-500">
              ENFOQUE
            </p>
            <h2 className="mt-5 text-4xl md:text-6xl font-semibold tracking-tight">
              Consistencia en textura.
              <span className="text-slate-400"> Estabilidad en proceso.</span>
            </h2>

            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-slate-600">
              Bloom 220 se usa cuando necesitas un gel con estructura definida, que
              responda de forma predecible durante preparación, reposo y almacenamiento.
            </p>
          </Reveal>

          <div className="mt-16 grid gap-14 md:grid-cols-3">
            <Reveal delay={60}>
              <div className="space-y-4">
                <div className="text-2xl md:text-3xl font-semibold tracking-tight">
                  Estructura
                </div>
                <p className="text-base leading-relaxed text-slate-600">
                  Aporta cuerpo y soporte a la formulación para mantener forma y acabado.
                </p>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <div className="space-y-4">
                <div className="text-2xl md:text-3xl font-semibold tracking-tight">
                  Control
                </div>
                <p className="text-base leading-relaxed text-slate-600">
                  Facilita un comportamiento uniforme del gel frente a variaciones del proceso.
                </p>
              </div>
            </Reveal>

            <Reveal delay={180}>
              <div className="space-y-4">
                <div className="text-2xl md:text-3xl font-semibold tracking-tight">
                  Repetibilidad
                </div>
                <p className="text-base leading-relaxed text-slate-600">
                  Resultados consistentes entre lotes cuando se mantienen condiciones de producción.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ========= STORY 2 (oscuro para contraste, sin CTA) ========= */}
      <section className="bg-slate-950 text-white">
        <div className="mx-auto max-w-7xl px-6 py-20 md:py-28">
          <Reveal>
            <p className="text-xs font-semibold tracking-[0.18em] text-white/60">
              APLICACIÓN
            </p>

            <h3 className="mt-5 text-4xl md:text-6xl font-semibold tracking-tight">
              Diseñado para formulaciones exigentes.
            </h3>

            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-white/70">
              Útil cuando el producto final debe conservar textura y forma con estabilidad,
              sin depender de ajustes constantes.
            </p>
          </Reveal>

          <div className="mt-16 grid gap-12 md:grid-cols-12 md:items-start">
            <div className="md:col-span-7">
              <Reveal delay={100}>
                <div className="space-y-10">
                  <div>
                    <p className="text-2xl font-semibold tracking-tight">
                      Estabilidad térmica (en proceso)
                    </p>
                    <p className="mt-3 text-white/70 leading-relaxed">
                      Comportamiento confiable ante variaciones moderadas de temperatura del proceso.
                    </p>
                  </div>

                  <div>
                    <p className="text-2xl font-semibold tracking-tight">
                      Preparación y reposo
                    </p>
                    <p className="mt-3 text-white/70 leading-relaxed">
                      Apoya la estructura durante preparación, reposo y almacenamiento.
                    </p>
                  </div>

                  <div>
                    <p className="text-2xl font-semibold tracking-tight">
                      Calidad constante
                    </p>
                    <p className="mt-3 text-white/70 leading-relaxed">
                      Reduce variabilidad y facilita controlar el acabado final del gel.
                    </p>
                  </div>
                </div>
              </Reveal>
            </div>

            <div className="md:col-span-5">
              <Reveal delay={160}>
                <div className="rounded-[34px] bg-white/5 p-10 md:p-12">
                  <p className="text-xs font-semibold tracking-[0.18em] text-white/60">
                    SOPORTE
                  </p>
                  <p className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight">
                    Enfoque B2B
                  </p>
                  <p className="mt-6 text-white/70 leading-relaxed">
                    Para empresas: trazabilidad por lote, documentación y orientación para escoger
                    presentación y mesh según tu proceso.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>

        <Divider tone="dark" />
      </section>

      {/* ========= STORY 3 (blanco, aplicaciones sin “brillar”) ========= */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 py-20 md:py-28">
          <Reveal>
            <p className="text-xs font-semibold tracking-[0.18em] text-slate-500">
              USOS FRECUENTES
            </p>
            <h3 className="mt-5 text-4xl md:text-6xl font-semibold tracking-tight">
              Aplicaciones típicas.
            </h3>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-slate-600">
              Referencias comunes para Bloom 220. La selección final depende de formulación,
              concentración y condiciones de proceso.
            </p>
          </Reveal>

          <div className="mt-16 grid gap-14 md:grid-cols-3">
            {[
              ["Confitería", "Gomitas, malvaviscos y gelificados con estructura definida."],
              ["Lácteos y postres", "Mousse, cremas y geles suaves con buena estabilidad."],
              ["Cárnicos / aspics", "Terrinas y gelificados con soporte y corte estable."],
            ].map(([t, d], i) => (
              <Reveal key={t} delay={80 + i * 80}>
                <div className="space-y-5">
                  <div className="text-2xl font-semibold tracking-tight">{t}</div>
                  <p className="text-base leading-relaxed text-slate-600">{d}</p>
                  <div className="pt-6">
                    <div className="h-px bg-black/5" />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Nota final minimal (sin CTA) */}
          <Reveal delay={140} className="mt-20">
            <div className="rounded-[34px] border border-black/5 bg-gradient-to-b from-slate-50 to-white px-8 py-10 md:px-12 md:py-12">
              <p className="text-xs font-semibold tracking-[0.18em] text-slate-500">
                NOTA
              </p>
              <p className="mt-4 text-lg leading-relaxed text-slate-600 max-w-3xl">
                Si compartes tu aplicación (tipo de producto, concentración objetivo y método de
                disolución), podemos orientar la presentación y el mesh más conveniente.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
